import React, { useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSystem } from "@/lib/system";
import { Panel, Badge } from "@/components/Bits";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, ClipboardCheck, ArrowRight, Info } from "lucide-react";

export default function BankResponse() {
  const { user } = useAuth();
  const { system } = useSystem();
  const canImport = ["admin", "supervisor"].includes(user?.role);
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [scopeSystem, setScopeSystem] = useState(system || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setErr("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const runPreview = async (f) => {
    setBusy(true); setErr(""); setResult(null); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (scopeSystem) fd.append("system", scopeSystem);
      const { data } = await api.post("/import/bank-response/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setPreview(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy(false); }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    await runPreview(f);
  };

  const apply = async () => {
    if (!file) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (scopeSystem) fd.append("system", scopeSystem);
      const { data } = await api.post("/import/bank-response", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setResult(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy(false); }
  };

  if (!canImport) {
    return <div className="p-8 text-slate-400 text-sm" data-testid="bank-response-forbidden">Hanya admin/supervisor yang dapat mengimpor bank response.</div>;
  }

  return (
    <div className="p-6 space-y-4 max-w-5xl animate-slide-up" data-testid="bank-response-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Data Sync</div>
        <h1 className="text-2xl font-bold tracking-tight">Bank Response Import</h1>
        <p className="text-xs text-slate-400 mt-1 max-w-3xl">
          Upload <span className="font-mono text-white">Ayolinx Ops Transaction Issue</span> sheet dari bank.
          Sistem akan auto-match tiap baris ke tiket eksisting (via <span className="text-cyan">Merchant Transaction ID</span> / <span className="text-cyan">RRN</span> / <span className="text-cyan">VA Number</span>)
          dan otomatis update <Badge color="success">SUKSES → RESOLVED · SUCCESS</Badge> <Badge color="error">GAGAL → RESOLVED · FAILED</Badge> <Badge color="warning">Raised/Pending → ESCALATED_TO_BANK</Badge>.
          Setiap update menghasilkan comment audit di tiket. Sheet yang di-support: <span className="font-mono">QRIS Issues</span>, <span className="font-mono">VA Issues</span>.
        </p>
      </div>

      <Panel title="Step 1 — Upload Bank Response Workbook" testid="step-upload">
        {!file && (
          <>
            <label className="block border-2 border-dashed border-line rounded-sm p-10 text-center cursor-pointer hover:border-cyan/50 hover:bg-surface2/30 transition-colors" data-testid="upload-dropzone">
              <Upload className="w-8 h-8 mx-auto mb-3 text-slate-500" />
              <div className="text-sm font-medium mb-1">Pilih file .xlsx dari bank</div>
              <div className="text-xs text-slate-500">Max 20 MB · sheet &quot;QRIS Issues&quot; &amp; &quot;VA Issues&quot;</div>
              <input ref={fileRef} type="file" accept=".xlsx" onChange={onPickFile} className="hidden" data-testid="upload-file-input" />
            </label>
            <div className="mt-3 flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Scope System</label>
              <select
                data-testid="scope-system-select"
                value={scopeSystem}
                onChange={(e) => setScopeSystem(e.target.value)}
                className="bg-bg border border-line rounded-sm px-2 py-1 text-xs focus:border-cyan focus:outline-none"
              >
                <option value="">All Systems</option>
                <option value="TARAPAY">Tarapay only</option>
                <option value="SATURNUS">Saturnuspay only</option>
              </select>
              <div className="text-[10px] text-slate-500 flex items-center gap-1"><Info className="w-3 h-3" /> Membatasi match ke system tertentu (recommended).</div>
            </div>
          </>
        )}
        {file && (
          <div className="flex items-center justify-between bg-surface2 border border-line rounded-sm p-3" data-testid="file-info">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-5 h-5 text-cyan" />
              <div>
                <div className="text-sm font-medium">{file.name}</div>
                <div className="text-[10px] text-slate-500 font-mono">{(file.size / 1024).toFixed(1)} KB · scope: {scopeSystem || "ALL"}</div>
              </div>
            </div>
            <button data-testid="reset-btn" onClick={reset} className="text-xs text-slate-400 hover:text-rose-400 border border-line px-3 py-1 rounded-sm">Reset</button>
          </div>
        )}
      </Panel>

      {err && <div data-testid="bank-response-err" className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm">{err}</div>}

      {busy && !preview && (
        <div className="text-xs text-slate-400 flex items-center gap-2" data-testid="parsing-msg">
          <div className="w-3 h-3 border-2 border-cyan border-t-transparent rounded-full animate-spin" />
          Parsing workbook &amp; matching tickets...
        </div>
      )}

      {preview && (
        <>
          <Panel title="Step 2 — Preview (dry-run)" testid="step-preview">
            <div className="grid grid-cols-4 gap-3 mb-4">
              <StatBox label="Total Rows" value={preview.total_rows} color="slate" testid="preview-total" />
              <StatBox label="Matched" value={preview.total_matched} color="emerald" testid="preview-matched" />
              <StatBox label="Unmatched" value={preview.total_unmatched} color="amber" testid="preview-unmatched" />
              <StatBox label="Will Update" value={preview.total_will_update} color="cyan" testid="preview-will-update" />
            </div>

            <div className="space-y-3">
              {preview.sheets.map((s) => (
                <div key={s.sheet} className="border border-line rounded-sm p-3" data-testid={`sheet-preview-${s.sheet}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold">{s.sheet}</span>
                      <Badge color="info">{s.kind.toUpperCase()}</Badge>
                      <span className="text-[10px] text-slate-500">{s.row_count} rows</span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-emerald-400">✓ {s.matched} matched</span>
                      <span className="text-amber-400">✗ {s.unmatched} unmatched</span>
                      <span className="text-cyan">↻ {s.will_update} will update</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Action Breakdown</div>
                      <div className="space-y-0.5">
                        {Object.entries(s.action_counts).map(([a, n]) => (
                          <div key={a} className="flex items-center justify-between text-[11px] font-mono">
                            <span className={a === "FINAL_SUCCESS" ? "text-emerald-400" : a === "FINAL_FAILED" ? "text-rose-400" : a === "STILL_PENDING" ? "text-amber-400" : "text-slate-500"}>{a}</span>
                            <span className="text-slate-300">{n}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Unmatched Samples</div>
                      <div className="space-y-0.5 max-h-32 overflow-y-auto">
                        {s.unmatched_samples.length === 0 && <div className="text-[11px] text-slate-500">—</div>}
                        {s.unmatched_samples.map((u, i) => (
                          <div key={i} className="text-[10px] font-mono text-slate-400 truncate" title={JSON.stringify(u)}>
                            r{u.row}: {u.merchant_transaction_id || u.rrn || u.va_number || u.reason}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {preview.skipped_sheets?.length > 0 && (
              <div className="mt-3 text-[10px] text-slate-500">
                Skipped sheets: <span className="font-mono">{preview.skipped_sheets.join(", ")}</span>
              </div>
            )}
          </Panel>

          <div className="flex justify-end">
            <button
              data-testid="apply-btn"
              onClick={apply}
              disabled={busy || preview.total_will_update === 0}
              className="bg-cyan text-bg font-semibold px-6 py-2.5 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark disabled:opacity-50"
            >
              <ClipboardCheck className="w-4 h-4" />
              {busy ? "Applying..." : `Apply Updates to ${preview.total_will_update} Tickets`}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </>
      )}

      {result && (
        <Panel title="Import Result" testid="import-result">
          <div className="grid grid-cols-4 gap-3 mb-4">
            <StatBox label="Total Rows" value={result.total_rows} color="slate" testid="result-total" />
            <StatBox label="Matched" value={result.matched} color="emerald" testid="result-matched" />
            <StatBox label="Updated" value={result.updated} color="cyan" testid="result-updated" />
            <StatBox label="Unmatched" value={result.unmatched} color="amber" testid="result-unmatched" />
          </div>

          {Object.keys(result.per_sheet).length > 0 && (
            <div className="space-y-1 mb-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Per Sheet</div>
              {Object.entries(result.per_sheet).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs border border-line rounded-sm px-3 py-1.5" data-testid={`result-sheet-${k}`}>
                  <span className="font-mono">{k}</span>
                  <span className="text-slate-400">
                    <span className="text-emerald-400">✓ {v.matched} matched</span>
                    {" · "}
                    <span className="text-cyan">↻ {v.updated} updated</span>
                    {" · "}
                    <span className="text-slate-500">{v.no_change} no-change</span>
                    {" · "}
                    <span className="text-amber-400">✗ {v.unmatched} unmatched</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {result.unmatched_examples?.length > 0 && (
            <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3">
              <div className="flex items-center gap-2 text-amber-400 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="text-xs font-semibold">Unmatched examples (first {result.unmatched_examples.length})</span>
              </div>
              <div className="text-[10px] font-mono space-y-0.5 max-h-40 overflow-y-auto">
                {result.unmatched_examples.map((u, i) => (
                  <div key={i} className="text-slate-400 truncate">
                    [{u.sheet}] r{u.row}: mtid={u.merchant_transaction_id || "—"} rrn={u.rrn || "—"} va={u.va_number || "—"}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mt-4 text-emerald-400 text-xs">
            <CheckCircle2 className="w-4 h-4" />
            Selesai. Buka halaman Tickets untuk melihat perubahan status &amp; comment audit.
          </div>
        </Panel>
      )}
    </div>
  );
}

function StatBox({ label, value, color, testid }) {
  const cmap = {
    slate: "border-line bg-surface2 text-white",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-400",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-400",
    cyan: "border-cyan/30 bg-cyan/5 text-cyan",
  };
  return (
    <div className={`border rounded-sm p-3 ${cmap[color] || cmap.slate}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold font-mono mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}
